import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import api, { errorMessage } from "../../lib/api";
import { canOfferCopilot, openPage, pendingProposal, restoreMessages, taskFormProps } from "../../lib/copilot";
import { CopilotRequestError, streamQuestion } from "../../lib/copilotStream";
import { hasPermission } from "../../lib/permissions";
import useDirectory from "../../hooks/useDirectory";
import TaskFormSheet from "../TaskFormSheet";

const INTERRUPTED = "La réponse a été interrompue.";
let localId = 0;
const nextId = () => `local-${(localId += 1)}`;

const CopilotContext = createContext(null);

// The conversation under way survives a reload of the tab (sessionStorage: this tab only, per account). A per-viewer convenience:
// unreadable storage or a conversation gone simply starts a new one.
const storageKey = (userId) => `jamora.copilot.conversation.${userId}`;

function rememberedConversation(userId) {
  try {
    return window.sessionStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function rememberConversation(userId, id) {
  try {
    if (id) window.sessionStorage.setItem(storageKey(userId), id);
    else window.sessionStorage.removeItem(storageKey(userId));
  } catch {
    // storage blocked: the conversation is just not restored after a reload
  }
}

/** The conversation of the Copilote Jamora, shared by its two surfaces: the bubble's panel and the full page (/copilote). Mounted
 * once in Layout, so a conversation started in the bubble continues on the page (« agrandir ») and survives page changes. */
export function CopilotProvider({ children }) {
  const { user } = useAuth();
  if (!canOfferCopilot(user)) return children;
  return <Session>{children}</Session>;
}

/** null when the Copilote is not offered to the signed-in person. */
export function useCopilotSession() {
  return useContext(CopilotContext);
}

function Session({ children }) {
  const { user } = useAuth();
  const [view, setView] = useState("chat"); // the panel's own toggle: conversation or history
  const [availability, setAvailability] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [title, setTitle] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deciding, setDeciding] = useState(null); // id of the proposal whose decision is being sent
  const [editing, setEditing] = useState(null); // proposal opened in its usual form (« Modifier »)
  const directory = useDirectory(Boolean(editing) && (hasPermission(user, "tasks:scope_all") || hasPermission(user, "tasks:edit")));
  const location = useLocation();
  const pageRef = useRef(null);
  pageRef.current = openPage(location);
  const inputRef = useRef(null); // the question field of whichever surface is shown (never both at once)
  const abortRef = useRef(null);
  const conversationRef = useRef(null);
  conversationRef.current = conversationId;
  const restoredRef = useRef(false); // nothing is remembered before the reload's conversation was given its chance

  const refreshAvailability = useCallback(() => {
    api.get("/assistant/me").then(({ data }) => setAvailability(data)).catch(() => setAvailability(null));
  }, []);

  const focusInput = useCallback(() => window.setTimeout(() => inputRef.current?.focus(), 50), []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const updateAnswer = (id, change) => setMessages((list) => list.map((m) => (m.id === id ? change(m) : m)));

  // A question, or the resumption after the person decided on a card (`resumeAfter`: the server tells the assistant the decision).
  const runTurn = async ({ question, resumeAfter }) => {
    const answerId = nextId();
    setView("chat");
    setMessages((list) => [
      // A new question instead of an answer lapses the waiting card (the server does the same).
      ...(question ? list.map((m) => (m.proposal?.status === "pending" ? { ...m, proposal: { ...m.proposal, status: "cancelled" } } : m)) : list),
      ...(question ? [{ id: nextId(), role: "user", text: question }] : []),
      { id: answerId, role: "assistant", text: "", error: null },
    ]);
    setStreaming(true);
    setStatus(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamQuestion({
        text: question, resumeAfter, conversationId: conversationRef.current, page: pageRef.current, signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "conversation") {
            setConversationId(event.id);
            setTitle((current) => current || event.title);
            setConversations(null); // the list is read again: the new conversation goes on top
          } else if (event.type === "text") {
            setStatus(null);
            updateAnswer(answerId, (m) => ({ ...m, text: m.text + event.delta }));
          } else if (event.type === "status") {
            setStatus(event.label ? `${event.label}…` : null);
          } else if (event.type === "error") {
            updateAnswer(answerId, (m) => ({ ...m, error: event.message }));
          } else if (event.type === "proposal") {
            updateAnswer(answerId, (m) => ({ ...m, proposal: event.proposal }));
          }
        },
      });
    } catch (error) {
      if (error.name === "AbortError") return;
      const message = error instanceof CopilotRequestError ? error.message : "Impossible de joindre le Copilote. Vérifiez votre connexion.";
      updateAnswer(answerId, (m) => ({ ...m, error: message }));
    } finally {
      setStreaming(false);
      setStatus(null);
      abortRef.current = null;
      refreshAvailability(); // the day's spending (and a cap just reached) follow each answer
    }
  };

  const ask = (question) => runTurn({ question });

  const replaceProposal = (updated) => setMessages((list) => list.map((m) => (m.proposal?.id === updated.id ? { ...m, proposal: updated } : m)));

  // The person's click, outside the model: the server re-checks everything, runs the screen's own route, then the conversation
  // resumes so the assistant can confirm or propose the next action.
  const decide = async (proposal, send) => {
    setDeciding(proposal.id);
    try {
      const { data } = await send();
      replaceProposal(data);
    } catch (e) {
      toast.error(errorMessage(e, "La décision n'a pas pu être envoyée. Réessayez."));
      return;
    } finally {
      setDeciding(null);
    }
    await runTurn({ resumeAfter: proposal.id });
  };

  // `fileIds`: what the person joined on the card (already sent through the screen's upload route), only for an action that takes files.
  // `body`: what the person set on the card — the joined files, the lines of a batch they unticked, the shared value they edited.
  const acceptProposal = (proposal, body) => decide(proposal, () => api.post(`/assistant/proposals/${proposal.id}/accept`, body));
  const refuseProposal = (proposal, reason) => decide(proposal, () => api.post(`/assistant/proposals/${proposal.id}/refuse`, { reason: reason?.trim() || null }));
  const editProposal = (proposal) => setEditing(proposal);
  const savedFromForm = () => {
    const proposal = editing;
    setEditing(null);
    decide(proposal, () => api.post(`/assistant/proposals/${proposal.id}/edited`));
  };
  const completeFromForm = async (task, payload) => {
    try {
      await api.patch(`/tasks/${task.id}`, payload);
      toast.success("Tâche mise à jour");
      savedFromForm();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'enregistrement"));
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setTitle(null);
    setMessages([]);
    setView("chat");
    focusInput();
  };

  const loadConversations = useCallback(() => {
    setLoadingHistory(true);
    api.get("/assistant/conversations").then(({ data }) => setConversations(data.items))
      .catch((e) => { toast.error(errorMessage(e, "Impossible de charger vos conversations.")); setConversations([]); })
      .finally(() => setLoadingHistory(false));
  }, []);

  const toggleHistory = () => {
    if (view === "history") {
      setView("chat");
      return;
    }
    setView("history");
    loadConversations();
  };

  const showConversation = (data) => {
    setConversationId(data.id);
    setTitle(data.title);
    setMessages(restoreMessages(data.messages, data.proposals).map(({ errorCode, ...m }) => ({ ...m, error: errorCode ? INTERRUPTED : null })));
  };

  const openConversation = async (id) => {
    try {
      const { data } = await api.get(`/assistant/conversations/${id}`);
      showConversation(data);
      setView("chat");
      focusInput();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'ouvrir cette conversation."));
      loadConversations();
    }
  };

  // After a reload: back to the conversation the tab was on, without a toast if it is gone (expired, deleted elsewhere).
  useEffect(() => {
    const id = rememberedConversation(user.id);
    if (!id) {
      restoredRef.current = true;
      return;
    }
    api.get(`/assistant/conversations/${id}`)
      .then(({ data }) => {
        if (!conversationRef.current) showConversation(data); // a question already asked meanwhile wins
      })
      .catch(() => rememberConversation(user.id, null))
      .finally(() => {
        restoredRef.current = true;
        if (conversationRef.current) rememberConversation(user.id, conversationRef.current);
      });
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (restoredRef.current) rememberConversation(user.id, conversationId);
  }, [user.id, conversationId]);

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/assistant/conversations/${id}`);
      setConversations((list) => (list || []).filter((c) => c.id !== id));
      if (id === conversationRef.current) newConversation();
      toast.success("Conversation supprimée");
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer cette conversation."));
    }
  };

  const form = taskFormProps(editing);
  const value = {
    state: { view, messages, streaming, status, availability, conversations, loadingHistory, title, deciding, conversationId },
    actions: { ask, newConversation, toggleHistory, openConversation, deleteConversation, acceptProposal, refuseProposal, editProposal,
               loadConversations, refreshAvailability, focusInput },
    inputRef,
    waitingCard: pendingProposal(messages),
  };

  return (
    <CopilotContext.Provider value={value}>
      {children}
      {form && (
        <TaskFormSheet open={Boolean(editing)} onOpenChange={(open) => { if (!open) setEditing(null); }} task={form.task}
          initialValues={form.initialValues} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={hasPermission(user, "tasks:edit")}
          directory={directory} onSaved={savedFromForm} onComplete={completeFromForm} />
      )}
    </CopilotContext.Provider>
  );
}
